/**
 * Client for the Jira-like /api/v2 surface.
 *
 * Everything is org-scoped through `/api/v2/orgs/{slug}/...`. The slug is the
 * only tenancy input the client supplies, and the server resolves it against
 * the caller's own membership — a non-member gets the same 404 a non-existent
 * workspace produces, so nothing here can widen its own scope.
 *
 * CSRF is NOT handled here. `@/lib/api` attaches `X-CSRF-Token` to every unsafe
 * method from its own token store and, on a 419, refreshes the token and
 * replays the request once. Setting the header here as well would pin the stale
 * value onto that replay and turn a recoverable 419 into a failed write.
 */

import { API_BASE_URL, api } from '@/lib/api'
import type { Project, UUID } from '@/types/api'

import type {
  ActivityResponse,
  BacklogResponse,
  BoardResponse,
  BulkResponse,
  BulkSetInput,
  ColumnCreateInput,
  ColumnPatchInput,
  ColumnsResponse,
  Comment,
  Issue,
  IssueDetail,
  IssueListResponse,
  IssueWriteInput,
  LabelView,
  MembersResponse,
  MoveIssueInput,
  SessionResponse,
  SprintView,
  SprintWriteInput,
} from './types'

function orgBase(slug: string): string {
  return `/api/v2/orgs/${encodeURIComponent(slug)}`
}

/** Comma-joins a multi-select filter; the server splits on commas. */
function list(values: readonly string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined
  return values.join(',')
}

export interface IssueQuery {
  project?: string
  q?: string
  type?: readonly string[]
  status?: readonly string[]
  priority?: readonly string[]
  label?: readonly string[]
  assignee?: string
  reporter?: string
  sprint?: string
  parent?: string
  column_id?: UUID
  sort?: string
  limit?: number
  cursor?: string
  include_epics?: boolean
}

export const v2Api = {
  /* -------------------------------------------------------------- session */

  session: () => api.get<SessionResponse>('/api/v2/auth/session'),

  members: (slug: string) => api.get<MembersResponse>(`${orgBase(slug)}/members`),

  /* --------------------------------------------------------------- issues */

  listIssues: (slug: string, query: IssueQuery = {}) =>
    api.get<IssueListResponse>(`${orgBase(slug)}/issues`, {
      query: {
        project: query.project,
        q: query.q,
        type: list(query.type),
        status: list(query.status),
        priority: list(query.priority),
        label: list(query.label),
        assignee: query.assignee,
        reporter: query.reporter,
        sprint: query.sprint,
        parent: query.parent,
        column_id: query.column_id,
        sort: query.sort,
        limit: query.limit,
        cursor: query.cursor,
        include_epics: query.include_epics,
      },
    }),

  getIssue: (slug: string, ref: string) =>
    api.get<IssueDetail>(`${orgBase(slug)}/issues/${encodeURIComponent(ref)}`),

  createIssue: (slug: string, projectKey: string, input: IssueWriteInput) =>
    api.post<Issue>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/issues`,
      input,
    ),

  patchIssue: (slug: string, ref: string, input: IssueWriteInput) =>
    api.patch<Issue>(
      `${orgBase(slug)}/issues/${encodeURIComponent(ref)}`,
      input,
    ),

  deleteIssue: (slug: string, ref: string) =>
    api.delete<{ message: string }>(
      `${orgBase(slug)}/issues/${encodeURIComponent(ref)}`,
    ),

  /** The drop endpoint: the server computes the rank from the two neighbours. */
  moveIssue: (slug: string, ref: string, input: MoveIssueInput) =>
    api.post<Issue>(
      `${orgBase(slug)}/issues/${encodeURIComponent(ref)}/move`,
      input,
    ),

  bulkIssues: (
    slug: string,
    input: { issue_keys: string[]; action?: 'update' | 'delete'; set?: BulkSetInput },
  ) => api.post<BulkResponse>(`${orgBase(slug)}/issues/bulk`, input),

  setIssueLabels: (slug: string, ref: string, labels: string[]) =>
    api.put<Issue>(
      `${orgBase(slug)}/issues/${encodeURIComponent(ref)}/labels`,
      { labels },
    ),

  /* ---------------------------------------------------------------- board */

  board: (slug: string, projectKey: string, scope: string) =>
    api.get<BoardResponse>(`${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/board`, {
      query: { sprint: scope },
    }),

  backlog: (slug: string, projectKey: string, includeCompleted = false) =>
    api.get<BacklogResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/backlog`,
      { query: { include_completed: includeCompleted ? 'true' : undefined } },
    ),

  /* -------------------------------------------------------------- columns */

  /*
   * The column registry. Every one of these answers the same
   * `ColumnsResponse`, so a write replaces the cached set outright rather than
   * merging one row into it — deleting a column re-homes its issues and can
   * promote a new default, and a client that patched a single row would be
   * quietly out of step with the board.
   *
   * The server owns the rules these calls can fail on, and its messages are
   * written to be shown: the last column of a legacy status cannot be deleted
   * or re-mapped, and adding a column beyond the seeded three needs a paid
   * plan (a 402 the UI turns into an upgrade prompt).
   */

  columns: (slug: string, projectKey: string) =>
    api.get<ColumnsResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/columns`,
    ),

  createColumn: (slug: string, projectKey: string, input: ColumnCreateInput) =>
    api.post<ColumnsResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/columns`,
      input,
    ),

  patchColumn: (slug: string, projectKey: string, columnId: UUID, input: ColumnPatchInput) =>
    api.patch<ColumnsResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/columns/${encodeURIComponent(columnId)}`,
      input,
    ),

  /** `moveTo` is the column the issues land in; omitted, the server uses the
   *  default column of the deleted column's own status. */
  deleteColumn: (slug: string, projectKey: string, columnId: UUID, moveTo?: UUID | null) =>
    api.delete<ColumnsResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/columns/${encodeURIComponent(columnId)}`,
      { query: { move_to: moveTo ?? undefined } },
    ),

  /** The whole set, in the new order. A partial list is refused. */
  reorderColumns: (slug: string, projectKey: string, ids: UUID[]) =>
    api.post<ColumnsResponse>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/columns/reorder`,
      { ids },
    ),

  /* -------------------------------------------------------------- sprints */

  sprints: (slug: string, projectKey: string, includeCompleted = true) =>
    api.get<{ data: SprintView[] }>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/sprints`,
      { query: { include_completed: includeCompleted ? 'true' : 'false' } },
    ),

  createSprint: (slug: string, projectKey: string, input: SprintWriteInput) =>
    api.post<SprintView>(
      `${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/sprints`,
      input,
    ),

  startSprint: (slug: string, sprintId: UUID) =>
    api.post<SprintView>(`${orgBase(slug)}/sprints/${sprintId}/start`, {}),

  completeSprint: (slug: string, sprintId: UUID, moveTo?: UUID | 'backlog') =>
    api.post<{ sprint: SprintView; carried_over: number; carried_to: string | null }>(
      `${orgBase(slug)}/sprints/${sprintId}/complete`,
      moveTo ? { move_to: moveTo } : {},
    ),

  /* ------------------------------------------------------ project logo */

  // Multipart, so no Content-Type is set by hand: the browser has to add the
  // multipart boundary, and setting the header manually omits it and the
  // request arrives unparseable.
  uploadProjectLogo: (slug: string, projectKey: string, file: File) => {
    const body = new FormData()
    body.append('logo', file)
    return api.post<Project>(`${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/logo`, body)
  },

  deleteProjectLogo: (slug: string, projectKey: string) =>
    api.delete<Project>(`${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/logo`),

  // The bytes are served by the API, not from a public path, so that every read
  // is checked against the caller's membership.
  projectLogoUrl: (slug: string, projectKey: string) =>
    `${API_BASE_URL}${orgBase(slug)}/projects/${encodeURIComponent(projectKey)}/logo`,

  // Deleting a sprint does NOT delete its issues: the server returns them to
  // the backlog in the same transaction. That is the fact the confirmation
  // dialog has to say out loud, because "delete sprint" reads like it takes the
  // work with it.
  deleteSprint: (slug: string, sprintId: UUID) =>
    api.delete<{ message: string }>(`${orgBase(slug)}/sprints/${sprintId}`),

  changeSprintScope: (slug: string, sprintId: UUID, input: { add?: string[]; remove?: string[] }) =>
    api.post<{ added: number; removed: number }>(
      `${orgBase(slug)}/sprints/${sprintId}/issues`,
      input,
    ),

  /* ------------------------------------------------------ comments & feed */

  comments: (slug: string, ref: string) =>
    api.get<{ data: Comment[] }>(`${orgBase(slug)}/issues/${encodeURIComponent(ref)}/comments`),

  createComment: (slug: string, ref: string, body: string) =>
    api.post<Comment>(
      `${orgBase(slug)}/issues/${encodeURIComponent(ref)}/comments`,
      { body },
    ),

  patchComment: (slug: string, commentId: UUID, body: string) =>
    api.patch<Comment>(`${orgBase(slug)}/comments/${commentId}`, { body }),

  deleteComment: (slug: string, commentId: UUID) =>
    api.delete<{ message: string }>(`${orgBase(slug)}/comments/${commentId}`),

  activity: (slug: string, ref: string, before?: number) =>
    api.get<ActivityResponse>(`${orgBase(slug)}/issues/${encodeURIComponent(ref)}/activity`, {
      query: { before },
    }),

  /* --------------------------------------------------------------- labels */

  labels: (slug: string) => api.get<{ data: LabelView[] }>(`${orgBase(slug)}/labels`),

  createLabel: (slug: string, input: { name: string; color?: string }) =>
    api.post<LabelView>(`${orgBase(slug)}/labels`, input),

  updateLabel: (slug: string, labelId: string, input: { name?: string; color?: string }) =>
    api.patch<LabelView>(`${orgBase(slug)}/labels/${encodeURIComponent(labelId)}`, input),

  // Deleting a label detaches it from every issue that carried it; the issues
  // themselves are untouched. The count in the confirm dialog is what makes
  // that distinction visible before the click, not after.
  deleteLabel: (slug: string, labelId: string) =>
    api.delete<{ message: string }>(`${orgBase(slug)}/labels/${encodeURIComponent(labelId)}`),
}
